import type { CustomerRecord } from '@/domain/types';

/**
 * The one-line summary a Messenger's push carries to the Agent's card (B8 / 75nf,
 * diagram 03 "conversation summary"). Rule-built, no AI (Anas, 1 Oct: no AI tool;
 * the Messenger edits it before pushing). Facts come from the structured brief
 * when it's filled, else from simple keyword matches over what the client wrote,
 * then the client's last words, clipped.
 */

const STYLE_WORDS: Array<[RegExp, string]> = [
  [/\bfine[\s-]?line\b/i, 'fine line'],
  [/\bblack\s?work\b/i, 'blackwork'],
  [/\brealis[mt]\b/i, 'realism'],
  [/\bscript|lettering\b/i, 'script'],
  [/\bneo[\s-]?trad/i, 'neo-traditional'],
  [/\b(?<!neo[\s-]?)trad(itional)?\b/i, 'traditional'],
  [/\bgeometric\b/i, 'geometric'],
  [/\bcover[\s-]?up\b/i, 'cover-up'],
];

const PLACEMENT_WORDS = [
  'forearm',
  'upper arm',
  'wrist',
  'sleeve',
  'shoulder',
  'back',
  'chest',
  'ribs',
  'hip',
  'thigh',
  'calf',
  'shin',
  'ankle',
  'foot',
  'hand',
  'finger',
  'neck',
  'arm',
  'leg',
];

const SIZE_WORDS: Array<[RegExp, string]> = [
  [/\bpalm[\s-]?size[d]?\b/i, 'palm size'],
  [/\bhalf[\s-]?day\b/i, 'half day'],
  [/\bfull[\s-]?day\b/i, 'full day'],
  [/\bsmall\b/i, 'small'],
  [/\blarge|big\b/i, 'large'],
];

const COLOUR_WORDS: Array<[RegExp, string]> = [
  [/\bblack\s?(and|&|n)\s?gr[ae]y\b/i, 'black and grey'],
  [/\bcolou?r(ed)?\b/i, 'colour'],
];

const QUOTE_CLIP = 110;

function firstMatch(text: string, table: Array<[RegExp, string]>): string | undefined {
  return table.find(([re]) => re.test(text))?.[1];
}

function clip(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length > QUOTE_CLIP ? `${flat.slice(0, QUOTE_CLIP - 1)}…` : flat;
}

export function buildPushSummary(customer: Pick<CustomerRecord, 'conversation' | 'designIntent'>): string {
  const inbound = customer.conversation.filter((e) => e.direction === 'in' && e.channel !== 'voice');
  const said = inbound.map((e) => e.body).join('  ');
  const di = customer.designIntent;

  const facts: string[] = [];
  const style = di?.type ? di.type.replace(/-/g, ' ') : firstMatch(said, STYLE_WORDS);
  if (style) facts.push(style);
  const placement = di?.bodyPart
    ? di.bodyPart.replace(/-/g, ' ')
    : PLACEMENT_WORDS.find((w) => new RegExp(`\\b${w}s?\\b`, 'i').test(said));
  if (placement) facts.push(placement);
  const size = di?.size ? di.size.replace(/-/g, ' ') : firstMatch(said, SIZE_WORDS);
  if (size) facts.push(size);
  const colour = di?.colour ? di.colour.replace(/-/g, ' ') : firstMatch(said, COLOUR_WORDS);
  if (colour) facts.push(colour);

  const last = inbound.reduce<(typeof inbound)[number] | undefined>(
    (latest, e) => (!latest || e.timestamp > latest.timestamp ? e : latest),
    undefined,
  );
  const head = facts.length > 0 ? `${facts[0]!.charAt(0).toUpperCase()}${facts[0]!.slice(1)}${facts.length > 1 ? ` · ${facts.slice(1).join(' · ')}` : ''}.` : '';
  const quote = last ? `Last said: “${clip(last.body)}”` : '';
  return [head, quote].filter(Boolean).join(' ');
}
